import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Event } from "./EventService";
import { Room } from "./RoomService";
import { User } from "./UserService";

// GraphQL queries
const EVENT_TEAM_LIST_QUERY = gql`
  query EventTeamList($filter: EventTeamFilter, $params: PaginationAndSorting) {
    eventTeamList(filter: $filter, params: $params) {
      code
      data {
        id
        eventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        updatedAt
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_TEAM_GET_QUERY = gql`
  query EventTeamGet($id: ID) {
    eventTeamGet(id: $id) {
      code
      data {
        id
        eventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_TEAM_GET_MANY_QUERY = gql`
  query EventTeamGetMany($ids: [ID]) {
    eventTeamGetMany(ids: $ids) {
      code
      data {
        id
        eventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EVENT_TEAM_CREATE_MUTATION = gql`
  mutation EventTeamCreate($params: EventTeamInput) {
    eventTeamCreate(params: $params) {
      code
      data {
        id
        eventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_TEAM_UPDATE_MUTATION = gql`
  mutation EventTeamUpdate(
    $where: EventTeamWhereInput
    $params: EventTeamInput
  ) {
    eventTeamUpdate(where: $where, params: $params) {
      code
      data {
        id
        eventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_TEAM_REMOVE_MUTATION = gql`
  mutation EventTeamRemove($where: EventTeamWhereInput) {
    eventTeamRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_TEAM_REMOVE_MANY_MUTATION = gql`
  mutation EventTeamRemoveMany($where: [EventTeamWhereInput]) {
    eventTeamRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface EventTeam {
  id: string;
  eventId?: string;
  teamName?: string;
  teamType?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  notes?: string;
  orderNo?: string;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  event?: Event;
  supportingRooms?: Room[];
}

export interface EventTeamFilter {
  id?: string[];
  eventId?: string[];
  teamName?: string;
  teamType?: string[];
  contactPerson?: string;
  email?: string;
}

export interface EventTeamInput {
  id?: string | null | undefined;
  eventId?: string | null | undefined;
  teamName?: string | null | undefined;
  teamType?: string | null | undefined;
  contactPerson?: string | null | undefined;
  phone?: string | null | undefined;
  email?: string | null | undefined;
  notes?: string | null | undefined;
  orderNo?: string | null | undefined;
  supportingRooms?: string[] | null | undefined;
}

export interface EventTeamWhereInput {
  id: string;
}

/**
 * Event Team service that handles all event team-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class EventTeamService {
  /**
   * Get a list of event teams with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EventTeamFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_TEAM_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single event team by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_TEAM_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple event teams by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_TEAM_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new event team
   */
  static async create(params: EventTeamInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_TEAM_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing event team
   */
  static async update(id: string, params: EventTeamInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_TEAM_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an event team
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_TEAM_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple event teams
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EVENT_TEAM_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
